import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { doc, onSnapshot, setDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { auth, db } from '../../config/firebaseConfig';
import styles from './GridStriker.module.css';
import gsIcardi from '../../assets/soccer/gs/icardi.png';
import gsOsimhen from '../../assets/soccer/gs/osimhen.png';
import gsSane from '../../assets/soccer/gs/sane.png';
import gsTorreira from '../../assets/soccer/gs/torreira.png';
import rmBellingham from '../../assets/soccer/realmadrid/bellingham.png';
import rmMbappe from '../../assets/soccer/realmadrid/mbappe.png';
import rmValverde from '../../assets/soccer/realmadrid/valverde.png';
import rmVini from '../../assets/soccer/realmadrid/vini.png';

// Oyuncu fotoğrafları → taş sırası [GK, 1, 2, 3]
const GS_PLAYERS = [gsTorreira, gsIcardi, gsOsimhen, gsSane];
const RM_PLAYERS = [rmValverde, rmBellingham, rmVini, rmMbappe];

// Bu modülü görebilecek tek kullanıcılar (küçük harf). p0 = ilk e-posta, p1 = ikinci.
export const GRID_PLAYERS = [
  'turanemre1999@gmail.com',
  'suleyman.dogal@habcarecro.com'
];

const PLAYER_NAMES = {
  'turanemre1999@gmail.com': 'Emre',
  'suleyman.dogal@habcarecro.com': 'Süleyman'
};

// ================= FORMALAR (sadece görsel) =================
// body: forma deseni (CSS gradient) · trim: kenar rengi · num: forma numarası rengi · head: ten rengi
const JERSEYS = {
  galatasaray: { name: 'Galatasaray', body: 'linear-gradient(100deg,#E30613 0 52%,#FDB913 52%)', trim: '#FDB913', num: '#fff', head: '#e8b07a', players: GS_PLAYERS },
  fenerbahce:  { name: 'Fenerbahçe',  body: 'repeating-linear-gradient(90deg,#143C7B 0 25%,#FFE000 25% 50%)', trim: '#FFE000', num: '#fff', head: '#e8b07a' },
  besiktas:    { name: 'Beşiktaş',    body: 'repeating-linear-gradient(90deg,#111 0 25%,#fff 25% 50%)', trim: '#111', num: '#E30613', head: '#e8b07a' },
  realmadrid:  { name: 'Real Madrid', body: 'linear-gradient(180deg,#fff,#e9edf5)', trim: '#FEBE10', num: '#1b3a6b', head: '#e8b07a', players: RM_PLAYERS },
  barcelona:   { name: 'Barcelona',   body: 'repeating-linear-gradient(90deg,#004D98 0 25%,#A50044 25% 50%)', trim: '#FFED02', num: '#FFED02', head: '#e8b07a' }
};
const JERSEY_IDS = ['galatasaray', 'fenerbahce', 'besiktas', 'realmadrid', 'barcelona'];
const DEFAULT_JERSEYS = { p0: 'galatasaray', p1: 'fenerbahce' };
const jerseyTheme = (id) => JERSEYS[id] || JERSEYS.galatasaray;
const jerseyThemesFallback = { p0: jerseyTheme(DEFAULT_JERSEYS.p0), p1: jerseyTheme(DEFAULT_JERSEYS.p1) };

// ================= TAHTA & KURALLAR =================
const COLS = 11;           // x: 0..10 (saha boyu: her iki kenara birer kare eklendi)
const ROWS = 7;            // y: 0..6
const GOAL_ROWS = [2, 3, 4];
const CENTER_X = Math.floor(COLS / 2); // başlama / orta hücre
const RANGE = 3;           // pas / şut menzili (hücre)
const WIN_GOALS = 3;       // ilk N gol kazanır
const PIECES = 4;          // oyuncu başına taş

const PRESENCE_INTERVAL_MS = 15000;
const PRESENCE_TIMEOUT_MS = 45000;
const GOAL_CELEBRATION_MS = 2200;

const GAME_DOC = doc(db, 'GridStriker', 'duel');
const STATS_DOC = doc(db, 'GridStriker', 'rivalry');

// ================= REKABET (HEAD-TO-HEAD) =================
const SEASON_TARGET = 5; // sezonu kazanmak için gereken maç galibiyeti

const emptyStats = () => ({
  totals: { p0: { w: 0, g: 0 }, p1: { w: 0, g: 0 }, matches: 0 },
  season: { no: 1, p0: 0, p1: 0 },
  trophies: { p0: 0, p1: 0 },
  streak: { holder: null, count: 0 },
  history: [],
  achievements: { p0: [], p1: [] },
  recordedSeq: 0
});

const normalizeStats = (data) => {
  const s = { ...emptyStats(), ...(data || {}) };
  s.totals = {
    p0: { w: s.totals?.p0?.w ?? 0, g: s.totals?.p0?.g ?? 0 },
    p1: { w: s.totals?.p1?.w ?? 0, g: s.totals?.p1?.g ?? 0 },
    matches: s.totals?.matches ?? 0
  };
  s.season = { no: s.season?.no ?? 1, p0: s.season?.p0 ?? 0, p1: s.season?.p1 ?? 0 };
  s.trophies = { p0: s.trophies?.p0 ?? 0, p1: s.trophies?.p1 ?? 0 };
  s.streak = { holder: s.streak?.holder ?? null, count: s.streak?.count ?? 0 };
  s.history = Array.isArray(s.history) ? s.history : [];
  s.achievements = { p0: s.achievements?.p0 || [], p1: s.achievements?.p1 || [] };
  s.recordedSeq = s.recordedSeq ?? 0;
  return s;
};

// Rozet tanımları
const ACHIEVEMENTS = {
  firstWin:   { icon: '🎉', name: 'İlk Galibiyet', desc: 'İlk maçını kazandın!' },
  cleanSheet: { icon: '🧤', name: 'Gol Yemeden', desc: 'Kalede gol görmeden kazandın.' },
  streak3:    { icon: '🔥', name: 'Seri Galip', desc: 'Üst üste 3 galibiyet!' },
  wins10:     { icon: '🏅', name: '10 Galibiyet', desc: 'Toplam 10 maç kazandın.' },
  goals50:    { icon: '⚽', name: '50 Gol', desc: 'Toplam 50 gol attın.' },
  champion:   { icon: '👑', name: 'Sezon Şampiyonu', desc: 'Bir sezonu kazandın!' }
};

// Bir galibiyetten sonra yeni açılan rozetleri hesapla
const detectAchievements = (s, winnerKey, conceded, wonSeason) => {
  const have = new Set(s.achievements[winnerKey] || []);
  const add = [];
  const t = s.totals[winnerKey];
  const tryAdd = (id, cond) => { if (cond && !have.has(id)) add.push(id); };
  tryAdd('firstWin', t.w >= 1);
  tryAdd('cleanSheet', conceded === 0);
  tryAdd('streak3', s.streak.holder === winnerKey && s.streak.count >= 3);
  tryAdd('wins10', t.w >= 10);
  tryAdd('goals50', t.g >= 50);
  tryAdd('champion', wonSeason);
  return add;
};

// Saf: mevcut istatistiğe bir maç sonucu işle → { next, newAchievements }
const applyResult = (prev, winnerKey, scoreP0, scoreP1) => {
  const s = normalizeStats(prev);
  const loserKey = other(winnerKey);
  s.totals[winnerKey].w += 1;
  s.totals.p0.g += scoreP0;
  s.totals.p1.g += scoreP1;
  s.totals.matches += 1;
  s.streak = s.streak.holder === winnerKey
    ? { holder: winnerKey, count: s.streak.count + 1 }
    : { holder: winnerKey, count: 1 };
  s.season = { ...s.season, [winnerKey]: s.season[winnerKey] + 1 };
  let wonSeason = false;
  if (s.season[winnerKey] >= SEASON_TARGET) {
    s.trophies[winnerKey] += 1;
    s.season = { no: s.season.no + 1, p0: 0, p1: 0 };
    wonSeason = true;
  }
  const conceded = winnerKey === 'p0' ? scoreP1 : scoreP0;
  const newAchievements = detectAchievements(s, winnerKey, conceded, wonSeason);
  s.achievements = { ...s.achievements, [winnerKey]: [...(s.achievements[winnerKey] || []), ...newAchievements] };
  s.history = [{ winner: winnerKey, s0: scoreP0, s1: scoreP1, ts: Date.now() }, ...s.history].slice(0, 12);
  return { next: s, newAchievements, loserKey };
};

const sign = (n) => (n > 0 ? 1 : n < 0 ? -1 : 0);
const inBounds = (x, y) => x >= 0 && x < COLS && y >= 0 && y < ROWS;
const cheby = (a, b) => Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
const other = (p) => (p === 'p0' ? 'p1' : 'p0');
// p0 sağa hücum eder (kale x=COLS-1), p1 sola (kale x=0)
const goalX = (p) => (p === 'p0' ? COLS - 1 : 0);
const isGoalCell = (p, x, y) => x === goalX(p) && GOAL_ROWS.includes(y);

// ---- Diziliş & başlangıç ---- (P1, COLS'a göre P0'ın aynası)
const formationP0 = () => [{ x: 1, y: 3 }, { x: 3, y: 1 }, { x: 3, y: 3 }, { x: 3, y: 5 }];
const formationP1 = () => [{ x: COLS - 2, y: 3 }, { x: COLS - 4, y: 1 }, { x: COLS - 4, y: 3 }, { x: COLS - 4, y: 5 }];

// Başlama vuruşu: topu orta hücreye, vuran takımın ortadaki taşının üstüne koy.
const kickoff = (kicker) => {
  const p0 = formationP0();
  const p1 = formationP1();
  if (kicker === 'p0') {
    p0[2] = { x: CENTER_X, y: 3 };
    return { pieces: { p0, p1 }, ball: { x: CENTER_X, y: 3, owner: 'p0' } };
  }
  p1[2] = { x: CENTER_X, y: 3 };
  return { pieces: { p0, p1 }, ball: { x: CENTER_X, y: 3, owner: 'p1' } };
};

const emptyGame = () => ({
  status: 'idle',                 // idle | playing | finished
  turn: 'p0',
  pieces: { p0: formationP0(), p1: formationP1() },
  ball: { x: CENTER_X, y: 3, owner: null },
  scores: { p0: 0, p1: 0 },
  winner: null,
  seq: 0,
  lastAction: null,
  kickoffNext: null,       // gol sonrası: kutlama bitince başlama vuruşunu yapacak taraf
  tackleLockedFor: null,   // topu az önce kaptıran taraf bir tur müdahale yapamaz
  dribbleStreak: { p0: 0, p1: 0 }, // üst üste sürüş sayısı (en fazla 2)
  jerseys: { ...DEFAULT_JERSEYS }, // takım formaları (yalnız görsel)
  presence: {}
});

const MAX_DRIBBLE_STREAK = 2; // 3. ardışık sürüş yasak

const normalize = (data) => {
  const g = { ...emptyGame(), ...(data || {}) };
  g.pieces = {
    p0: (g.pieces?.p0 || formationP0()).map((c) => ({ x: c.x, y: c.y })),
    p1: (g.pieces?.p1 || formationP1()).map((c) => ({ x: c.x, y: c.y }))
  };
  g.ball = { x: g.ball?.x ?? CENTER_X, y: g.ball?.y ?? 3, owner: g.ball?.owner ?? null };
  g.scores = { p0: g.scores?.p0 ?? 0, p1: g.scores?.p1 ?? 0 };
  g.dribbleStreak = { p0: g.dribbleStreak?.p0 ?? 0, p1: g.dribbleStreak?.p1 ?? 0 };
  g.jerseys = { p0: g.jerseys?.p0 || DEFAULT_JERSEYS.p0, p1: g.jerseys?.p1 || DEFAULT_JERSEYS.p1 };
  return g;
};

const clone = (g) => ({
  ...g,
  pieces: { p0: g.pieces.p0.map((c) => ({ ...c })), p1: g.pieces.p1.map((c) => ({ ...c })) },
  ball: { ...g.ball },
  scores: { ...g.scores }
});

const pieceAt = (g, x, y) => {
  for (const p of ['p0', 'p1']) {
    const i = g.pieces[p].findIndex((c) => c.x === x && c.y === y);
    if (i !== -1) return { player: p, idx: i };
  }
  return null;
};

const isCarrier = (g, player, idx) => {
  const c = g.pieces[player][idx];
  return g.ball.owner === player && g.ball.x === c.x && g.ball.y === c.y;
};

// ---- Bir vuruşu (pas/şut) çöz: top, hedefe doğru hücre hücre ilerler ----
// result: 'goal' | 'intercept' | 'receive' | 'loose'  ·  blocked: kurtarış mı?
const resolveKick = (g, player, to) => {
  const b = g.ball;
  const dx = sign(to.x - b.x);
  const dy = sign(to.y - b.y);
  const steps = Math.max(Math.abs(to.x - b.x), Math.abs(to.y - b.y));
  // Düz 8 yön ve menzil kontrolü
  if (steps < 1 || steps > RANGE) return null;
  if (b.x + dx * steps !== to.x || b.y + dy * steps !== to.y) return null;

  for (let s = 1; s <= steps; s++) {
    const cx = b.x + dx * s;
    const cy = b.y + dy * s;
    if (!inBounds(cx, cy)) return { result: 'loose', cell: { x: b.x + dx * (s - 1), y: b.y + dy * (s - 1) }, owner: null };
    const occ = pieceAt(g, cx, cy);
    if (occ) {
      if (occ.player === player) return { result: 'receive', cell: { x: cx, y: cy }, owner: player };
      // rakip taş yolu kesti — kaledeyse kurtarış, değilse top kapma
      return { result: 'intercept', cell: { x: cx, y: cy }, owner: occ.player, save: isGoalCell(player, cx, cy) };
    }
    if (isGoalCell(player, cx, cy)) return { result: 'goal', cell: { x: cx, y: cy }, owner: null };
    if (s === steps) return { result: 'loose', cell: { x: cx, y: cy }, owner: null };
  }
  return null;
};

// ---- Seçili taş için yasal hamleler ----
const targetsFor = (g, player, idx) => {
  const out = { moves: [], dribbles: [], kicks: [], tackles: [] };
  const me = g.pieces[player][idx];
  const carrier = isCarrier(g, player, idx);
  const opp = other(player);

  if (carrier) {
    // Sürüş: 1 adım boş hücreye, rakibe komşu OLMAYAN yere.
    // Üst üste 2 sürüş yapıldıysa 3.'sü yasak — bu tur pas/şut atılmalı.
    const canDribble = (g.dribbleStreak?.[player] ?? 0) < MAX_DRIBBLE_STREAK;
    if (canDribble) {
      for (let ddx = -1; ddx <= 1; ddx++) {
        for (let ddy = -1; ddy <= 1; ddy++) {
          if (!ddx && !ddy) continue;
          const nx = me.x + ddx;
          const ny = me.y + ddy;
          if (!inBounds(nx, ny) || pieceAt(g, nx, ny)) continue;
          const nearOpp = g.pieces[opp].some((c) => cheby(c, { x: nx, y: ny }) <= 1);
          if (!nearOpp) out.dribbles.push({ x: nx, y: ny });
        }
      }
    }
    // Pas / şut: her 8 yönde menzile kadar net hücreler.
    // 1 hücre = sürüş bölgesi (yukarıda mavi); pas/şut yalnız 2+ hücre uzağa.
    // Tek istisna: hemen yanındaki boş kale → point-blank şut atılabilsin.
    const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
    for (const [dx, dy] of dirs) {
      for (let s = 1; s <= RANGE; s++) {
        const cx = me.x + dx * s;
        const cy = me.y + dy * s;
        if (!inBounds(cx, cy)) break;
        const occ = pieceAt(g, cx, cy);
        if (occ) {
          if (s >= 2) {
            const outcome = occ.player === player ? 'receive'
              : isGoalCell(player, cx, cy) ? 'save' : 'intercept';
            out.kicks.push({ x: cx, y: cy, outcome });
          }
          break; // taşı geçemez (1 hücrede olsa bile yol kapanır)
        }
        const goal = isGoalCell(player, cx, cy);
        if (s >= 2 || goal) {
          out.kicks.push({ x: cx, y: cy, outcome: goal ? 'goal' : 'loose' });
        }
      }
    }
  } else {
    // Hareket: 1 adım boş hücreye (boştaki topu kapabilir)
    for (let ddx = -1; ddx <= 1; ddx++) {
      for (let ddy = -1; ddy <= 1; ddy++) {
        if (!ddx && !ddy) continue;
        const nx = me.x + ddx;
        const ny = me.y + ddy;
        if (!inBounds(nx, ny) || pieceAt(g, nx, ny)) continue;
        const pickup = g.ball.owner === null && g.ball.x === nx && g.ball.y === ny;
        out.moves.push({ x: nx, y: ny, pickup });
      }
    }
    // Top kapma: dik komşuda rakip top taşıyıcısı varsa
    // (topu az önce kaptıran taraf bu tur müdahale yapamaz — arka arkaya kapışma yok)
    if (g.tackleLockedFor !== player) {
      const orth = [[1, 0], [-1, 0], [0, 1], [0, -1]];
      for (const [dx, dy] of orth) {
        const nx = me.x + dx;
        const ny = me.y + dy;
        if (g.ball.owner === opp && g.ball.x === nx && g.ball.y === ny) {
          out.tackles.push({ x: nx, y: ny });
        }
      }
    }
  }
  return out;
};

// ---- Gol kutlaması sonrası başlama vuruşunu uygula ----
const performKickoff = (g) => {
  const starter = g.kickoffNext;
  if (!starter) return g;
  const ko = kickoff(starter);
  const n = clone(g);
  n.pieces = ko.pieces;
  n.ball = ko.ball;
  n.turn = starter;
  n.kickoffNext = null;
  n.tackleLockedFor = null;
  n.dribbleStreak = { p0: 0, p1: 0 };
  n.seq = (g.seq || 0) + 1;
  n.lastAction = { type: 'kickoff', seq: n.seq };
  return n;
};

// ---- Hamleyi uygula (saf, deterministik) → { next, event } veya null (geçersiz) ----
const applyAction = (g, player, action) => {
  if (g.status !== 'playing' || g.turn !== player || g.kickoffNext) return null;
  const t = targetsFor(g, player, action.piece);
  const next = clone(g);
  const me = next.pieces[player][action.piece];
  const from = { x: me.x, y: me.y };
  const to = action.to;
  let event = null;

  if (action.type === 'move') {
    if (!t.moves.some((m) => m.x === to.x && m.y === to.y)) return null;
    me.x = to.x; me.y = to.y;
    if (next.ball.owner === null && next.ball.x === to.x && next.ball.y === to.y) {
      next.ball.owner = player; // boştaki topu kap
    }
    event = { type: 'move', player, from, to, result: 'move' };
  } else if (action.type === 'dribble') {
    if (!isCarrier(g, player, action.piece) || !t.dribbles.some((m) => m.x === to.x && m.y === to.y)) return null;
    me.x = to.x; me.y = to.y;
    next.ball.x = to.x; next.ball.y = to.y;
    event = { type: 'dribble', player, from, to, result: 'dribble' };
  } else if (action.type === 'tackle') {
    if (!t.tackles.some((m) => m.x === to.x && m.y === to.y)) return null;
    next.ball.x = me.x; next.ball.y = me.y; next.ball.owner = player; // topu komşu taşına çek
    event = { type: 'tackle', player, from: to, to: { x: me.x, y: me.y }, result: 'tackle' };
  } else if (action.type === 'pass') {
    if (!isCarrier(g, player, action.piece)) return null;
    const res = resolveKick(g, player, to);
    if (!res) return null;
    event = { type: 'pass', player, from, to: res.cell, result: res.result, save: !!res.save };
    if (res.result === 'goal') {
      next.scores[player] += 1;
      // Top kalede kalsın (içeri giriş animasyonu) — başlama vuruşu kutlamadan sonra.
      next.ball = { x: res.cell.x, y: res.cell.y, owner: null };
      if (next.scores[player] >= WIN_GOALS) {
        next.status = 'finished';
        next.winner = player;
        next.tackleLockedFor = null;
        next.dribbleStreak = { p0: 0, p1: 0 };
        next.seq = g.seq + 1;
        next.lastAction = { ...event, seq: next.seq };
        return { next, event };
      }
      next.kickoffNext = other(player); // gol yiyen, kutlama bitince başlar
    } else {
      next.ball = { x: res.cell.x, y: res.cell.y, owner: res.owner };
    }
  } else {
    return null;
  }

  next.turn = other(player);
  // Müdahale kilidi: kapan taraf, kaptıran tarafı bir sonraki turda müdahaleden men eder.
  // Oyuncu kendi (kilitli) turunu oynayınca kilit kalkar. Golde sıfırlanır.
  next.tackleLockedFor = action.type === 'tackle'
    ? other(player)
    : (g.tackleLockedFor === player ? null : g.tackleLockedFor);
  // Sürüş serisi: aynı oyuncu üst üste sürdükçe artar, başka hamlede sıfırlanır.
  next.dribbleStreak = {
    ...g.dribbleStreak,
    [player]: action.type === 'dribble' ? (g.dribbleStreak?.[player] ?? 0) + 1 : 0
  };
  if (event.result === 'goal') { next.tackleLockedFor = null; next.dribbleStreak = { p0: 0, p1: 0 }; }
  next.seq = g.seq + 1;
  next.lastAction = { ...event, seq: next.seq };
  return { next, event };
};

// ---- Antrenman için basit yapay zeka ----
const allActions = (g, player) => {
  const acts = [];
  g.pieces[player].forEach((c, i) => {
    const t = targetsFor(g, player, i);
    t.moves.forEach((m) => acts.push({ type: 'move', piece: i, to: { x: m.x, y: m.y }, pickup: m.pickup }));
    t.dribbles.forEach((m) => acts.push({ type: 'dribble', piece: i, to: m }));
    t.tackles.forEach((m) => acts.push({ type: 'tackle', piece: i, to: m }));
    t.kicks.forEach((m) => acts.push({ type: 'pass', piece: i, to: { x: m.x, y: m.y }, outcome: m.outcome }));
  });
  return acts;
};

// Zorluk profilleri: noise=rastgelelik, blunder=hata olasılığı, def=savunma ağırlığı
const AI_LEVELS = {
  easy:   { noise: 14, blunder: 0.32, def: 0.55 },
  normal: { noise: 2,  blunder: 0.05, def: 1.0 },
  hard:   { noise: 0.5, blunder: 0,   def: 1.45 }
};
const AI_LEVEL_OPTS = [
  { id: 'easy', name: 'Kolay', icon: '🐣' },
  { id: 'normal', name: 'Normal', icon: '⚽' },
  { id: 'hard', name: 'Zor', icon: '🔥' }
];

const aiChoose = (g, ai, level = 'normal') => {
  const cfg = AI_LEVELS[level] || AI_LEVELS.normal;
  const acts = allActions(g, ai);
  if (!acts.length) return null;
  const haveBall = g.ball.owner === ai;
  const oppHasBall = g.ball.owner === other(ai);
  const md = (a, b) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
  const fwd = (x) => (ai === 'p0' ? x : COLS - 1 - x);                       // büyük = ileri
  const vgoal = (y) => Math.min(...GOAL_ROWS.map((r) => Math.abs(r - y)));   // gol satırına dikey uzaklık
  const pieceOf = (a) => g.pieces[ai][a.piece];
  const goalAct = acts.find((a) => a.type === 'pass' && a.outcome === 'goal');

  // Kolay seviyede ara sıra bariz hata yap — ama net gol şansını her zaman değerlendir.
  if (cfg.blunder && Math.random() < cfg.blunder) {
    if (goalAct && Math.random() < 0.5) return goalAct;
    const safe = acts.filter((a) => !(a.type === 'pass' && a.outcome !== 'goal' && a.outcome !== 'receive' && a.outcome !== 'loose'));
    const pool = safe.length ? safe : acts;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  const score = (a) => {
    let s = Math.random() * cfg.noise;
    if (a.type === 'pass') {
      if (a.outcome === 'goal') return 1e6 + Math.random();                  // gol şansını kaçırma
      if (a.outcome === 'receive') {
        const adv = fwd(a.to.x) - fwd(g.ball.x);
        s += 80 + adv * 30 - vgoal(a.to.y) * 4;                              // ileri pas + gol bölgesi
      } else if (a.outcome === 'loose') {
        const adv = fwd(a.to.x) - fwd(g.ball.x);
        s += adv > 0 ? 25 + adv * 12 : -30;                                 // boşa pas yalnız ileriyse
      } else s -= 60;                                                        // top kaybı / kurtarış
    } else if (a.type === 'dribble') {
      const adv = fwd(a.to.x) - fwd(pieceOf(a).x);
      s += 60 + adv * 40 - vgoal(a.to.y) * 3;                               // topu sürerek ilerle
    } else if (a.type === 'tackle') {
      s += 450 + Math.random() * 30;                                        // topu kazan
    } else if (a.type === 'move') {
      if (a.pickup) s += 400;                                               // boştaki topu al
      else if (haveBall) {
        const adv = fwd(a.to.x) - fwd(pieceOf(a).x);                        // topsuz taşı outlet için öne koş
        s += 15 + adv * 14 - vgoal(a.to.y) * 2;
        if (fwd(a.to.x) > fwd(g.ball.x)) s += 18;                           // topun önüne geç
      } else if (oppHasBall) {
        s += (40 - md(a.to, { x: g.ball.x, y: g.ball.y }) * 5) * cfg.def;   // topa baskı (zorlukla artar)
        if (fwd(a.to.x) < fwd(g.ball.x)) s += 6 * cfg.def;                  // top ile kalen arasına gir
        if (vgoal(a.to.y) <= 1 && fwd(a.to.x) < fwd(g.ball.x)) s += 14 * cfg.def; // pas yolunu/kaleyi kapat
      } else {
        s += 60 - md(a.to, { x: g.ball.x, y: g.ball.y }) * 6;              // boştaki topa koş
      }
    }
    return s;
  };

  let best = acts[0];
  let bestS = -Infinity;
  for (const a of acts) {
    const sc = score(a);
    if (sc > bestS) { bestS = sc; best = a; }
  }
  return best;
};

// ================= SES (WebAudio, harici dosya yok) =================
const createSfx = () => {
  let ctx = null;
  let muted = false;
  const ac = () => {
    try {
      ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') ctx.resume();
      return ctx;
    } catch { return null; }
  };
  const tone = (freq, dur, type = 'sine', vol = 0.06, slideTo) => {
    const c = ac();
    if (!c || muted) return;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, c.currentTime);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, c.currentTime + dur);
    g.gain.setValueAtTime(vol, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
    o.connect(g); g.connect(c.destination); o.start(); o.stop(c.currentTime + dur);
  };
  const noise = (dur, vol = 0.08) => {
    const c = ac();
    if (!c || muted) return;
    const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const n = c.createBufferSource();
    const g = c.createGain();
    g.gain.value = vol; n.buffer = buf; n.connect(g); g.connect(c.destination); n.start();
  };
  // Sürekli stadyum uğultusu (kahverengi gürültü + alçak geçiren süzgeç)
  let ambient = null;
  const AMB_VOL = 0.02;
  const startAmbience = () => {
    const c = ac();
    if (!c || ambient) return;
    const dur = 2;
    const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
    const d = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < d.length; i++) { const w = Math.random() * 2 - 1; last = (last + 0.02 * w) / 1.02; d[i] = last * 3.2; }
    const src = c.createBufferSource(); src.buffer = buf; src.loop = true;
    const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 760;
    const g = c.createGain(); g.gain.value = muted ? 0 : AMB_VOL;
    src.connect(lp); lp.connect(g); g.connect(c.destination); src.start();
    ambient = { src, g };
  };
  const stopAmbience = () => { if (ambient) { try { ambient.src.stop(); } catch { /* noop */ } ambient = null; } };
  // Stadyum coşkusu — gol sonrası yükselen tezahürat
  const roar = () => {
    const c = ac();
    if (!c || muted) return;
    const dur = 1.2;
    const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      const t = i / d.length;
      const env = Math.min(1, t * 5) * (1 - Math.max(0, t - 0.4) / 0.6); // hızlı yüksel, yavaş düş
      d[i] = (Math.random() * 2 - 1) * env;
    }
    const src = c.createBufferSource(); src.buffer = buf;
    const bp = c.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 900; bp.Q.value = 0.7;
    const g = c.createGain(); g.gain.value = 0.12;
    src.connect(bp); bp.connect(g); g.connect(c.destination); src.start();
  };
  return {
    setMuted: (m) => { muted = m; if (ambient) ambient.g.gain.value = m ? 0 : AMB_VOL; },
    startAmbience, stopAmbience,
    whistle: () => { tone(2400, 0.15, 'square', 0.04); setTimeout(() => tone(2400, 0.4, 'square', 0.04), 200); },
    select: () => tone(680, 0.05, 'square', 0.03),
    pass: () => { noise(0.04, 0.08); tone(260, 0.1, 'sine', 0.07, 180); },
    dribble: () => tone(360, 0.08, 'triangle', 0.05),
    tackle: () => { tone(160, 0.18, 'sawtooth', 0.09, 70); noise(0.12, 0.06); },
    intercept: () => tone(300, 0.22, 'sawtooth', 0.05, 130),
    save: () => { tone(280, 0.25, 'sawtooth', 0.06, 120); noise(0.25, 0.05); },
    emote: () => tone(880, 0.08, 'sine', 0.04, 1200),
    goal: () => { [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => tone(f, 0.18, 'triangle', 0.07), i * 90)); roar(); },
    win: () => { [523, 659, 784, 1047, 1319].forEach((f, i) => setTimeout(() => tone(f, 0.22, 'triangle', 0.07), i * 120)); roar(); }
  };
};
const sfx = createSfx();

// ---- Spiker yorumları (rastgele seçilir) ----
const COMMENTARY = {
  goal: ['Ne gol ama! 🚀', 'GOOOL! Filenin köşesi! 🎯', 'Müthiş bir vuruş! 🔥', 'Ağları havalandırdı! ⚽', 'Kaleci çaresiz kaldı! 💥', 'Bu golü uzun süre konuşacağız! 🌟'],
  save: ['Müthiş kurtarış! 🧤', 'Kaleci devleşti! 🛡️', 'Köşeden çevirdi! ✋', 'Gole geçit yok! 🚫'],
  intercept: ['Top kapıldı! ⚡', 'Pas arası! 🦅', 'Araya girdi! ✋', 'Harika bir okuma! 🧠'],
  tackle: ['Sert ama temiz müdahale! 🦶', 'Topu söküp aldı! 💪', 'Çalımı yedirmedi! ✅']
};
const pickLine = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ================= GÖRSEL: TAHTA =================
const cellPct = (v, total) => ((v + 0.5) / total) * 100;

// ---- 3D mini kale (SVG) — sıfırdan: gerçek bir kutu/kafes ----
// Sol kale ağzı sahaya (sağa) bakar, ağ kenara (sola) doğru derinleşir.
// Sağ kale bunun aynası (scaleX -1). viewBox 100x400 = kutunun 1:4 oranı (üniform ölçek).
const Goal3D = ({ side }) => {
  // Kutunun 8 köşesi: taban (sahada) + U kadar yükseltilmiş tepe.
  // Izgaraya hizalı: ön ağız kale çizgisinde (yerel x≈100 = kale sütununun sağ kenarı),
  // ağız tam 3 kale satırını kaplar (yerel y 100→400 = 2.,3.,4. satır). Arka kenara daralır.
  const FTb = [95, 100], FBb = [95, 400];   // ön (ağız) taban — kale çizgisi, 2.→5. satır çizgisi
  const BTb = [13, 124], BBb = [13, 376];   // arka taban — kenar tarafı (perspektifle daralır)
  const U = [4, -70];                        // direk yüksekliği (yukarı + hafif sahaya)
  const up = (p) => [p[0] + U[0], p[1] + U[1]];
  const FTt = up(FTb), FBt = up(FBb), BTt = up(BTb), BBt = up(BBb);

  const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  const fmt = (p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`;
  const poly = (...pts) => pts.map(fmt).join(' ');
  const bar = (a, b, key, w, stroke, opacity) => (
    <line key={key} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]}
      stroke={stroke} strokeWidth={w} strokeOpacity={opacity} strokeLinecap="round" />
  );
  // Bir dörtgeni (A-B-C-D) ızgara telleriyle doldur (clipPath ile kırpılır).
  const mesh = (A, B, C, D, n1, n2, key) => {
    const out = [];
    for (let i = 1; i < n1; i++) {
      const t = i / n1, p = lerp(A, B, t), q = lerp(D, C, t);
      out.push(<line key={`${key}a${i}`} x1={p[0]} y1={p[1]} x2={q[0]} y2={q[1]} />);
    }
    for (let j = 1; j < n2; j++) {
      const t = j / n2, p = lerp(A, D, t), q = lerp(B, C, t);
      out.push(<line key={`${key}b${j}`} x1={p[0]} y1={p[1]} x2={q[0]} y2={q[1]} />);
    }
    return out;
  };

  const roof = poly(FTt, FBt, BBt, BTt);     // üst yüz (tavan)
  const back = poly(BTt, BBt, BBb, BTb);     // arka duvar
  const topS = poly(FTb, FTt, BTt, BTb);     // üst yan panel
  const botS = poly(FBb, FBt, BBt, BBb);     // alt yan panel
  const floor = poly(FTb, FBb, BBb, BTb);    // iç zemin (derinlik gölgesi)

  const pid = `gp-${side}`, fid = `gf-${side}`;
  const cR = `cr-${side}`, cB = `cb-${side}`, cT = `ct-${side}`, cBo = `cbo-${side}`;

  return (
    <svg
      className={styles.goalSvg}
      viewBox="0 0 100 400"
      preserveAspectRatio="xMidYMid meet"
      style={side === 'right' ? { transform: 'scaleX(-1)' } : undefined}
    >
      <defs>
        <linearGradient id={pid} x1="0" y1="0" x2="0.55" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.5" stopColor="#e9eef5" />
          <stop offset="1" stopColor="#aeb9c8" />
        </linearGradient>
        <radialGradient id={fid} cx="0.72" cy="0.42" r="0.95">
          <stop offset="0" stopColor="#0c2016" stopOpacity="0.04" />
          <stop offset="1" stopColor="#03100a" stopOpacity="0.5" />
        </radialGradient>
        <clipPath id={cR}><polygon points={roof} /></clipPath>
        <clipPath id={cB}><polygon points={back} /></clipPath>
        <clipPath id={cT}><polygon points={topS} /></clipPath>
        <clipPath id={cBo}><polygon points={botS} /></clipPath>
      </defs>

      {/* sahaya düşen yumuşak gölge */}
      <ellipse cx="52" cy="398" rx="44" ry="11" fill="rgba(0,0,0,0.32)" />

      {/* iç zemin (derinlik için hafif koyuluk) */}
      <polygon points={floor} fill={`url(#${fid})`} />

      {/* ===== AĞ (kafes panelleri, ince kare örgü) ===== */}
      <g fill="none" strokeLinecap="round">
        <g clipPath={`url(#${cB})`} stroke="rgba(255,255,255,0.5)" strokeWidth="0.7">
          {mesh(BTt, BBt, BBb, BTb, 9, 3, 'bk')}
        </g>
        <g clipPath={`url(#${cT})`} stroke="rgba(255,255,255,0.5)" strokeWidth="0.7">
          {mesh(FTb, FTt, BTt, BTb, 4, 7, 'ts')}
        </g>
        <g clipPath={`url(#${cBo})`} stroke="rgba(255,255,255,0.5)" strokeWidth="0.7">
          {mesh(FBb, FBt, BBt, BBb, 4, 7, 'bs')}
        </g>
        <g clipPath={`url(#${cR})`} stroke="rgba(255,255,255,0.4)" strokeWidth="0.7">
          {mesh(FTt, FBt, BBt, BTt, 6, 7, 'rf')}
        </g>
      </g>

      {/* ===== ÇERÇEVE ===== */}
      {/* arka çerçeve (ince, grimsi → uzakta) */}
      {bar(BTb, BTt, 'bp1', 3.5, '#c0cad8')}
      {bar(BBb, BBt, 'bp2', 3.5, '#c0cad8')}
      {bar(BTt, BBt, 'bcb', 3.5, '#cbd4e0')}

      {/* taban yan rayları (ağın yere tutunduğu kenarlar — soluk) */}
      {bar(FTb, BTb, 'g1', 2.4, '#dce4ee', 0.55)}
      {bar(FBb, BBb, 'g2', 2.4, '#dce4ee', 0.55)}

      {/* üst derinlik rayları (kutunun üst kenarları) */}
      {bar(FTt, BTt, 'r1', 4.5, `url(#${pid})`)}
      {bar(FBt, BBt, 'r2', 4.5, `url(#${pid})`)}

      {/* ÖN ÇERÇEVE (ağız) — en parlak, en kalın */}
      {bar(FTb, FTt, 'fp1', 8, `url(#${pid})`)}
      {bar(FBb, FBt, 'fp2', 8, `url(#${pid})`)}
      {bar(FTt, FBt, 'fcb', 8.5, `url(#${pid})`)}
      {/* ön çerçevede cam parıltısı */}
      {bar([FTt[0] - 2.4, FTt[1] + 4], [FBt[0] - 2.4, FBt[1] - 4], 'gl', 2, '#ffffff', 0.7)}
    </svg>
  );
};

const Board = ({ g, perspective, selected, targets, interactive, onCellClick, onPieceClick, celebrate, celebrateSide, jerseys = jerseyThemesFallback }) => {
  // perspective: 'p0' tahtayı normal, 'p1' 180° döndürerek kendi kalesini altta gösterir
  const flip = perspective === 'p1';
  const tx = (x) => (flip ? COLS - 1 - x : x);
  const ty = (y) => (flip ? ROWS - 1 - y : y);

  const targetMap = useMemo(() => {
    const m = new Map();
    if (!targets) return m;
    targets.moves.forEach((c) => m.set(`${c.x},${c.y}`, c.pickup ? 'pickup' : 'move'));
    targets.dribbles.forEach((c) => m.set(`${c.x},${c.y}`, 'dribble'));
    targets.tackles.forEach((c) => m.set(`${c.x},${c.y}`, 'tackle'));
    targets.kicks.forEach((c) => m.set(`${c.x},${c.y}`, `kick-${c.outcome}`));
    return m;
  }, [targets]);

  const confetti = useMemo(
    () => (celebrate ? Array.from({ length: 26 }, () => ({
      left: 10 + Math.random() * 80,
      dx: (Math.random() * 2 - 1) * 160,
      delay: Math.random() * 0.3,
      dur: 0.9 + Math.random() * 1,
      hue: Math.floor(Math.random() * 360)
    })) : []),
    [celebrate]
  );

  const cells = [];
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      const key = `${x},${y}`;
      const mark = targetMap.get(key);
      const goalSide = x === 0 || x === COLS - 1;
      const goalMouth = goalSide && GOAL_ROWS.includes(y);
      cells.push(
        <div
          key={key}
          className={[
            styles.cell,
            (x + y) % 2 === 0 ? styles.cellLight : styles.cellDark,
            goalMouth ? styles.goalMouth : '',
            mark ? styles.targetCell : ''
          ].join(' ')}
          style={{ gridColumn: tx(x) + 1, gridRow: ty(y) + 1 }}
          onClick={() => interactive && onCellClick(x, y)}
        >
          {mark && (
            <span className={`${styles.marker} ${styles[`m_${mark.replace('-', '_')}`] || ''}`}>
              {mark === 'tackle' ? '🦶' : mark === 'kick-goal' ? '🥅' : mark === 'kick-save' ? '🧤'
                : mark === 'pickup' ? '⚽' : mark === 'dribble' ? '👟'
                : mark === 'move' ? '·' : mark.startsWith('kick-intercept') ? '✕' : ''}
            </span>
          )}
        </div>
      );
    }
  }

  return (
    <div className={`${styles.boardWrap} ${celebrate ? styles.celebrate : ''}`}>
      <div className={styles.stadium}>
        {/* stadyum tribünleri — kullanıcı PNG'leri (üst + iki yan, alt yok) */}
        <div className={styles.standLeft} aria-hidden="true" />
        <div className={styles.standRight} aria-hidden="true" />
        <div className={styles.standTop} aria-hidden="true" />
        <div className={styles.board}>
        {/* zemin hücreleri */}
        <div className={styles.cellLayer}>{cells}</div>

        {/* saha çizgileri */}
        <div className={styles.pitchLines}>
          <span className={styles.halfLine} />
          <span className={styles.centerCircle} />
        </div>

        {/* kaleler (SVG 3D mini kale: ön direk + eğik üst/alt çerçeve + perspektif ağ) */}
        <div className={`${styles.goal} ${styles.goalLeft} ${celebrate ? styles.goalShake : ''}`}>
          <Goal3D side="left" />
        </div>
        <div className={`${styles.goal} ${styles.goalRight} ${celebrate ? styles.goalShake : ''}`}>
          <Goal3D side="right" />
        </div>

        {/* footballerlar */}
        {['p0', 'p1'].map((p) =>
          g.pieces[p].map((c, i) => {
            const isSel = selected && selected.player === p && selected.idx === i;
            const isTackleTarget = targetMap.get(`${c.x},${c.y}`) === 'tackle';
            const jersey = jerseys[p] || jerseyThemesFallback[p];
            const cheering = celebrateSide === p;
            return (
              <motion.div
                key={`${p}-${i}`}
                className={[
                  styles.piece,
                  isSel ? styles.pieceSelected : '',
                  isTackleTarget ? styles.pieceThreatened : ''
                ].join(' ')}
                initial={false}
                style={{ x: '-50%', y: '-50%' }}
                animate={{ left: `${cellPct(tx(c.x), COLS)}%`, top: `${cellPct(ty(c.y), ROWS)}%` }}
                transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                whileHover={interactive ? { scale: 1.14 } : undefined}
                whileTap={interactive ? { scale: 0.9 } : undefined}
                onClick={(e) => { e.stopPropagation(); interactive && onPieceClick(p, i, c.x, c.y); }}
              >
                <div className={`${styles.footballer} ${cheering ? styles.cheer : ''}`} style={{ animationDelay: `${(i * 0.3).toFixed(2)}s` }}>
                  <span className={styles.fShadow} />
                  {jersey.players?.[i] ? (
                    <img className={styles.fPhoto} src={jersey.players[i]} alt="" draggable="false" />
                  ) : (
                    <>
                      <span className={styles.fHead} style={{ background: jersey.head }} />
                      <span className={styles.fArmL} style={{ background: jersey.body }} />
                      <span className={styles.fArmR} style={{ background: jersey.body }} />
                      <span className={styles.fJersey} style={{ background: jersey.body, borderColor: jersey.trim, color: jersey.num }}>
                        <span className={styles.fNum}>{i === 0 ? 'GK' : i}</span>
                      </span>
                    </>
                  )}
                </div>
              </motion.div>
            );
          })
        )}

        {/* top — gol şutunda kaleye doğru daha yavaş/dramatik süzülür */}
        <motion.div
          className={`${styles.ball} ${celebrate ? styles.ballGoal : ''}`}
          initial={false}
          animate={{ left: `${cellPct(tx(g.ball.x), COLS)}%`, top: `${cellPct(ty(g.ball.y), ROWS)}%` }}
          transition={g.lastAction?.result === 'goal'
            ? { type: 'tween', ease: 'easeOut', duration: 0.7 }
            : { type: 'tween', ease: 'easeOut', duration: 0.35 }}
        >
          <span className={styles.ballSpin}>⚽</span>
        </motion.div>

        {confetti.map((c, i) => (
          <span
            key={i}
            className={styles.confetti}
            style={{
              left: `${c.left}%`,
              '--dx': `${c.dx}px`,
              background: `hsl(${c.hue} 90% 60%)`,
              animationDelay: `${c.delay}s`,
              animationDuration: `${c.dur}s`
            }}
          />
        ))}
        </div>
      </div>
    </div>
  );
};

// ================= FORMA SEÇİCİ =================
const JerseyPicker = ({ current, onPick, label = '👕 Forman' }) => (
  <div className={styles.jerseyPicker}>
    <span className={styles.jerseyLabel}>{label}</span>
    <div className={styles.jerseyRow}>
      {JERSEY_IDS.map((id) => {
        const j = JERSEYS[id];
        return (
          <button
            key={id}
            type="button"
            className={`${styles.jerseyChip} ${current === id ? styles.jerseyActive : ''}`}
            onClick={() => onPick(id)}
            title={j.name}
          >
            <span className={styles.jerseySwatch} style={{ background: j.body, borderColor: j.trim }}>
              <span className={styles.jerseyCollar} style={{ background: j.trim }} />
            </span>
            <span className={styles.jerseyName}>{j.name}</span>
          </button>
        );
      })}
    </div>
  </div>
);

// ================= REKABET PANELİ =================
const RivalryPanel = ({ stats, nameP0, nameP1, myKey, compact, collapsible, open = true, onToggle }) => {
  if (!stats) return null;
  const { totals, season, trophies, streak, history } = stats;
  const nameOf = (k) => (k === 'p0' ? nameP0 : nameP1);
  const pct = (v) => `${Math.min(100, (v / SEASON_TARGET) * 100)}%`;
  const tro = (n) => '🏆'.repeat(Math.min(n, 3));
  return (
    <div className={`${styles.rivalry} ${compact ? styles.rivalryCompact : ''} ${collapsible && !open ? styles.rivalryClosed : ''}`}>
      <div
        className={styles.rivalryHead}
        onClick={collapsible ? onToggle : undefined}
        style={collapsible ? { cursor: 'pointer' } : undefined}
        role={collapsible ? 'button' : undefined}
      >
        <span>⚔️ Ezeli Rekabet</span>
        <span className={styles.rivalryHeadRight}>
          <span className={styles.rivalrySeason}>Sezon {season.no}</span>
          {collapsible && <span className={styles.rivalryChevron}>{open ? '▾' : '▸'}</span>}
        </span>
      </div>
      {collapsible && !open ? (
        <div className={styles.rivalryMini}>{nameP0} {totals.p0.w}–{totals.p1.w} {nameP1}</div>
      ) : (
      <>
      <div className={styles.h2h}>
        <span className={styles.h2hName}>{nameP0} {trophies.p0 > 0 && <em>{tro(trophies.p0)}</em>}</span>
        <span className={styles.h2hScore}>{totals.p0.w}<i>–</i>{totals.p1.w}</span>
        <span className={`${styles.h2hName} ${styles.h2hRight}`}>{trophies.p1 > 0 && <em>{tro(trophies.p1)}</em>} {nameP1}</span>
      </div>
      <div className={styles.seasonBar}>
        <div className={styles.seasonFillL} style={{ width: pct(season.p0) }} />
        <div className={styles.seasonFillR} style={{ width: pct(season.p1) }} />
      </div>
      <div className={styles.seasonLabels}>
        <span>{season.p0}</span>
        <span>sezon yarışı · ilk {SEASON_TARGET} kazanır</span>
        <span>{season.p1}</span>
      </div>
      {streak.holder && streak.count >= 2 && (
        <div className={styles.streakLine}>🔥 {nameOf(streak.holder)} {streak.count} maçtır kazanıyor!</div>
      )}
      {!compact && history.length > 0 && (
        <div className={styles.histRow}>
          <span className={styles.histLabel}>Son maçlar</span>
          {history.slice(0, 8).map((h, i) => (
            <span key={i} className={`${styles.histPill} ${h.winner === myKey ? styles.histWin : styles.histLoss}`} title={`${h.s0}–${h.s1}`}>
              {h.winner === myKey ? 'G' : 'M'}
            </span>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
};

// ================= ANA BİLEŞEN =================
const GridStriker = ({ onClose }) => {
  const [email, setEmail] = useState(auth.currentUser?.email?.toLowerCase() || '');
  const [mode, setMode] = useState('menu'); // menu | online | practice
  const [game, setGame] = useState(emptyGame());
  const [practice, setPractice] = useState(null);
  const [selected, setSelected] = useState(null); // { player, idx }
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(false);
  const [celebrate, setCelebrate] = useState(null); // 'p0' | 'p1' | null
  const [banner, setBanner] = useState(null);        // { text, kind }
  const [practiceJerseys, setPracticeJerseys] = useState(() => {
    let mine = DEFAULT_JERSEYS.p0;
    let opp = 'realmadrid';
    try {
      mine = localStorage.getItem('habcaregol_jersey') || mine;
      opp = localStorage.getItem('habcaregol_oppjersey') || opp;
    } catch { /* ignore */ }
    return { p0: mine, p1: opp };
  });
  const [aiLevel, setAiLevel] = useState(() => {
    try { return localStorage.getItem('habcaregol_ailevel') || 'normal'; } catch { return 'normal'; }
  });
  const [stats, setStats] = useState(null);   // rekabet istatistikleri (online)
  const [achievement, setAchievement] = useState(null); // yeni rozet bildirimi
  const [rivalryOpen, setRivalryOpen] = useState(() => {
    try { return localStorage.getItem('habcaregol_rivalry_open') !== '0'; } catch { return true; }
  });

  const prevSeqRef = useRef(0);
  const celebTimerRef = useRef(null);
  const goalFxTimerRef = useRef(null);
  const bannerTimerRef = useRef(null);
  const aiTimerRef = useRef(null);
  const aiLevelRef = useRef(aiLevel);
  useEffect(() => { aiLevelRef.current = aiLevel; }, [aiLevel]);

  const myIndex = GRID_PLAYERS.indexOf(email);
  const allowed = myIndex !== -1;
  const myKey = `p${myIndex}`;
  const oppKey = other(myKey);
  const oppEmail = GRID_PLAYERS[1 - myIndex];
  const myName = PLAYER_NAMES[email] || 'Sen';
  const oppName = PLAYER_NAMES[oppEmail] || 'Rakip';
  const nameP0 = PLAYER_NAMES[GRID_PLAYERS[0]] || 'Oyuncu 1';
  const nameP1 = PLAYER_NAMES[GRID_PLAYERS[1]] || 'Oyuncu 2';

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => setEmail(u?.email?.toLowerCase() || ''));
    return () => unsub();
  }, []);

  useEffect(() => { sfx.setMuted(muted); }, [muted]);

  // Maç sürerken stadyum uğultusu (g henüz tanımlı değil — durumu kaynaktan oku)
  useEffect(() => {
    const playing = mode === 'practice' ? practice?.status === 'playing' : game.status === 'playing';
    if (playing) sfx.startAmbience(); else sfx.stopAmbience();
    return () => sfx.stopAmbience();
  }, [mode, game.status, practice?.status]);

  // Tam ekran: arka sayfanın kaymasını kilitle + ESC ile kapat
  useEffect(() => {
    if (!allowed) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [allowed, onClose]);

  // Online dinleyici + presence
  useEffect(() => {
    if (!allowed) return;
    const unsub = onSnapshot(GAME_DOC, (snap) => setGame(normalize(snap.data())),
      (err) => { console.error('GridStriker listener:', err); setError('Oyun verisi yüklenemedi.'); });
    return () => unsub();
  }, [allowed]);

  // Rekabet istatistikleri dinleyici
  useEffect(() => {
    if (!allowed) return;
    const unsub = onSnapshot(STATS_DOC, (snap) => setStats(normalizeStats(snap.data())),
      (err) => console.error('Stats listener:', err));
    return () => unsub();
  }, [allowed]);

  useEffect(() => {
    if (!allowed) return;
    const beat = async () => {
      try { await setDoc(GAME_DOC, { presence: { [myKey]: serverTimestamp() } }, { merge: true }); }
      catch (err) { console.error('Presence:', err); }
    };
    beat();
    const hb = setInterval(beat, PRESENCE_INTERVAL_MS);
    const tick = setInterval(() => setNow(Date.now()), 5000);
    return () => { clearInterval(hb); clearInterval(tick); };
  }, [allowed, myKey]);

  useEffect(() => () => {
    clearTimeout(celebTimerRef.current);
    clearTimeout(goalFxTimerRef.current);
    clearTimeout(bannerTimerRef.current);
    clearTimeout(aiTimerRef.current);
  }, []);

  // Online maç bitince sonucu rekabet kaydına işle (yalnız kazanan yazar; seq ile tekrar engellenir)
  useEffect(() => {
    if (!allowed || mode !== 'online') return;
    if (game.status !== 'finished' || !game.winner || game.winner !== myKey) return;
    const finishedSeq = game.seq;
    let unlocked = [];
    runTransaction(db, async (tx) => {
      const snap = await tx.get(STATS_DOC);
      const cur = normalizeStats(snap.data());
      if (cur.recordedSeq === finishedSeq) return; // zaten işlenmiş
      const { next, newAchievements } = applyResult(cur, game.winner, game.scores.p0, game.scores.p1);
      next.recordedSeq = finishedSeq;
      unlocked = newAchievements;
      tx.set(STATS_DOC, next, { merge: true });
    }).then(() => {
      if (unlocked.length) {
        setAchievement(ACHIEVEMENTS[unlocked[0]]);
        setTimeout(() => setAchievement(null), 4200);
      }
    }).catch((err) => console.error('record result:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, mode, game.status, game.winner, game.seq, myKey]);

  const isPractice = mode === 'practice';
  const g = isPractice ? (practice || emptyGame()) : game;
  const humanKey = isPractice ? 'p0' : myKey;
  const aiKey = 'p1';
  const myTurn = g.status === 'playing' && g.turn === humanKey;
  const canPlay = myTurn && !g.kickoffNext; // gol kutlaması sırasında hamle yok

  // ---- Formalar (yalnız görsel): aktif moda göre çöz ----
  const jerseyIds = isPractice ? practiceJerseys : (game.jerseys || DEFAULT_JERSEYS);
  const jerseyThemes = { p0: jerseyTheme(jerseyIds.p0), p1: jerseyTheme(jerseyIds.p1) };
  const myJerseyId = isPractice ? practiceJerseys.p0 : (game.jerseys?.[myKey] || DEFAULT_JERSEYS[myKey]);

  const selectJersey = async (id) => {
    sfx.select();
    setPracticeJerseys((prev) => ({ ...prev, p0: id }));
    try { localStorage.setItem('habcaregol_jersey', id); } catch { /* ignore */ }
    if (!isPractice) {
      try {
        await setDoc(GAME_DOC, { jerseys: { ...(game.jerseys || DEFAULT_JERSEYS), [myKey]: id } }, { merge: true });
      } catch (err) { console.error('selectJersey:', err); }
    }
  };

  // Antrenman: rakip (Robo) takımını seç
  const selectOppJersey = (id) => {
    sfx.select();
    setPracticeJerseys((prev) => ({ ...prev, p1: id }));
    try { localStorage.setItem('habcaregol_oppjersey', id); } catch { /* ignore */ }
  };

  const oppLastSeen = game.presence?.[oppKey];
  const oppOnline = !!oppLastSeen?.toMillis && (now - oppLastSeen.toMillis()) < PRESENCE_TIMEOUT_MS;

  // Seçili taşın hedefleri
  const targets = useMemo(
    () => (selected && selected.player === humanKey && canPlay ? targetsFor(g, humanKey, selected.idx) : null),
    [selected, g, humanKey, canPlay]
  );

  // Sıra değişince / mod değişince seçimi temizle
  useEffect(() => { setSelected(null); }, [g.turn, mode, g.status]);

  // Mod değişiminde son aksiyonu yeniden seslendirme/kutlama
  useEffect(() => { prevSeqRef.current = g.seq || 0; setCelebrate(null); setBanner(null); clearTimeout(goalFxTimerRef.current); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Aksiyon olaylarında ses + gol kutlaması (seq artışı) ----
  useEffect(() => {
    const seq = g.seq || 0;
    if (seq <= prevSeqRef.current) { prevSeqRef.current = seq; return; }
    prevSeqRef.current = seq;
    const a = g.lastAction;
    if (!a) return;
    if (a.result === 'goal') {
      const scorer = a.player === 'p0' ? leftName() : rightName();
      // Önce top ağa süzülsün, sonra kutlama patlasın.
      const GOAL_FLY_MS = 450;
      clearTimeout(goalFxTimerRef.current);
      clearTimeout(celebTimerRef.current);
      goalFxTimerRef.current = setTimeout(() => {
        sfx.goal();
        setCelebrate(a.player);
        flashBanner(`⚽ ${scorer} — ${pickLine(COMMENTARY.goal)}`, 'goal');
      }, GOAL_FLY_MS);
      celebTimerRef.current = setTimeout(() => setCelebrate(null), GOAL_CELEBRATION_MS - 150);
    } else if (a.result === 'intercept') {
      a.save ? sfx.save() : sfx.intercept();
      flashBanner(pickLine(a.save ? COMMENTARY.save : COMMENTARY.intercept), 'info');
    } else if (a.type === 'tackle') {
      sfx.tackle();
      flashBanner(pickLine(COMMENTARY.tackle), 'info');
    } else if (a.type === 'pass') {
      sfx.pass();
    } else if (a.type === 'dribble') {
      sfx.dribble();
    }
    if (g.status === 'finished') sfx.win();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g.seq]);

  const flashBanner = (text, kind) => {
    setBanner({ text, kind });
    clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setBanner(null), 1600);
  };

  // ---- İsim yardımcıları (perspektife göre tahtanın solu/sağı) ----
  const leftName = () => (isPractice ? 'Sen' : (myIndex === 0 ? myName : oppName));
  const rightName = () => (isPractice ? 'Robo' : (myIndex === 0 ? oppName : myName));

  // ---- Hamle uygula (online transaction / antrenman lokal) ----
  const perform = useCallback(async (action) => {
    setSelected(null);
    if (isPractice) {
      setPractice((prev) => {
        const r = applyAction(prev, humanKey, action);
        return r ? r.next : prev;
      });
      return;
    }
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(GAME_DOC);
        const data = normalize(snap.data());
        const r = applyAction(data, myKey, action);
        if (!r) return;
        const n = r.next;
        tx.set(GAME_DOC, {
          status: n.status, turn: n.turn, pieces: n.pieces, ball: n.ball,
          scores: n.scores, winner: n.winner, seq: n.seq, lastAction: n.lastAction,
          tackleLockedFor: n.tackleLockedFor, dribbleStreak: n.dribbleStreak,
          kickoffNext: n.kickoffNext ?? null,
          updatedAt: serverTimestamp()
        }, { merge: true });
      });
    } catch (err) {
      console.error('perform action:', err);
      setError('Hamle gönderilemedi. Tekrar deneyin.');
    }
  }, [isPractice, humanKey, myKey]);

  // ---- Gol sonrası başlama vuruşu (kutlama bitince) ----
  useEffect(() => {
    if (!g.kickoffNext || g.status !== 'playing') return;
    const t = setTimeout(() => {
      if (isPractice) {
        setPractice((prev) => (prev && prev.kickoffNext ? performKickoff(prev) : prev));
        return;
      }
      // Online: tek yazım yeter — transaction kickoffNext'i kontrol eder (ikinci deneme no-op).
      runTransaction(db, async (tx) => {
        const snap = await tx.get(GAME_DOC);
        const data = normalize(snap.data());
        if (data.status !== 'playing' || !data.kickoffNext) return;
        const n = performKickoff(data);
        tx.set(GAME_DOC, {
          status: n.status, turn: n.turn, pieces: n.pieces, ball: n.ball,
          scores: n.scores, winner: n.winner, seq: n.seq, lastAction: n.lastAction,
          tackleLockedFor: n.tackleLockedFor, dribbleStreak: n.dribbleStreak,
          kickoffNext: null, updatedAt: serverTimestamp()
        }, { merge: true });
      }).catch((err) => console.error('kickoff:', err));
    }, GOAL_CELEBRATION_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPractice, g.kickoffNext, g.seq, g.status]);

  // ---- Tahta tıklama mantığı ----
  const handleCell = (x, y) => {
    if (!canPlay) return;
    if (selected && targets) {
      if (targets.tackles.some((c) => c.x === x && c.y === y)) return perform({ type: 'tackle', piece: selected.idx, to: { x, y } });
      if (targets.kicks.some((c) => c.x === x && c.y === y)) return perform({ type: 'pass', piece: selected.idx, to: { x, y } });
      if (targets.dribbles.some((c) => c.x === x && c.y === y)) return perform({ type: 'dribble', piece: selected.idx, to: { x, y } });
      if (targets.moves.some((c) => c.x === x && c.y === y)) return perform({ type: 'move', piece: selected.idx, to: { x, y } });
    }
    // boş/diğer hücre → seçimi bırak
    const occ = pieceAt(g, x, y);
    if (occ && occ.player === humanKey) { sfx.select(); setSelected({ player: humanKey, idx: occ.idx }); }
    else setSelected(null);
  };

  const handlePiece = (player, idx, x, y) => {
    if (!canPlay) return;
    // Top kapma hedefi (rakip taşıyıcı) seçili taşımla tıklanırsa
    if (selected && targets?.tackles.some((c) => c.x === x && c.y === y)) {
      return perform({ type: 'tackle', piece: selected.idx, to: { x, y } });
    }
    if (selected && targets?.kicks.some((c) => c.x === x && c.y === y)) {
      return perform({ type: 'pass', piece: selected.idx, to: { x, y } });
    }
    if (player === humanKey) { sfx.select(); setSelected({ player: humanKey, idx }); }
    else setSelected(null);
  };

  // ---- Antrenman: yapay zeka sırası ----
  useEffect(() => {
    if (!isPractice || !practice || practice.status !== 'playing' || practice.turn !== aiKey || practice.kickoffNext) return;
    aiTimerRef.current = setTimeout(() => {
      setPractice((prev) => {
        if (!prev || prev.status !== 'playing' || prev.turn !== aiKey || prev.kickoffNext) return prev;
        const a = aiChoose(prev, aiKey, aiLevelRef.current);
        if (!a) return prev;
        const r = applyAction(prev, aiKey, a);
        return r ? r.next : prev;
      });
    }, 650 + Math.random() * 500);
    return () => clearTimeout(aiTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPractice, practice?.turn, practice?.seq, practice?.status]);

  // ---- Online aksiyonlar ----
  const startMatch = async (starter) => {
    try {
      const ko = kickoff(starter);
      await setDoc(GAME_DOC, {
        status: 'playing', turn: starter, pieces: ko.pieces, ball: ko.ball,
        scores: { p0: 0, p1: 0 }, winner: null, tackleLockedFor: null, kickoffNext: null,
        dribbleStreak: { p0: 0, p1: 0 }, jerseys: game.jerseys || DEFAULT_JERSEYS,
        seq: (game.seq || 0) + 1,
        lastAction: { type: 'kickoff', seq: (game.seq || 0) + 1 },
        presence: game.presence || {}, updatedAt: serverTimestamp()
      }, { merge: true });
      sfx.whistle();
    } catch (err) { console.error('startMatch:', err); setError('Maç başlatılamadı.'); }
  };

  const resetOnline = async () => {
    try {
      await setDoc(GAME_DOC, {
        ...emptyGame(), jerseys: game.jerseys || DEFAULT_JERSEYS, seq: (game.seq || 0) + 1,
        presence: game.presence || {}, updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (err) { console.error('resetOnline:', err); }
  };

  const startPractice = () => {
    const ko = kickoff('p0');
    setPractice({ ...emptyGame(), status: 'playing', turn: 'p0', pieces: ko.pieces, ball: ko.ball });
    setMode('practice');
    setCelebrate(null);
    prevSeqRef.current = 0;
    sfx.whistle();
  };

  const selectAiLevel = (lvl) => {
    sfx.select();
    setAiLevel(lvl);
    try { localStorage.setItem('habcaregol_ailevel', lvl); } catch { /* ignore */ }
  };

  const toggleRivalry = () => {
    sfx.select();
    setRivalryOpen((o) => {
      const n = !o;
      try { localStorage.setItem('habcaregol_rivalry_open', n ? '1' : '0'); } catch { /* ignore */ }
      return n;
    });
  };

  const leaveToMenu = () => {
    if (mode === 'online' && g.status !== 'idle') resetOnline();
    setMode('menu');
    setPractice(null);
    setSelected(null);
    setCelebrate(null);
  };

  if (!allowed) return null;

  // ---- Durum metni ----
  const winnerName = g.winner ? (g.winner === 'p0' ? leftName() : rightName()) : null;
  const iWon = !isPractice && g.winner === myKey;
  const ballOwnerName = g.ball.owner ? (g.ball.owner === 'p0' ? leftName() : rightName()) : null;

  const statusLine = g.status !== 'playing' ? null
    : g.kickoffNext ? '⚽ GOOOL! Yeniden başlıyor...'
    : myTurn
      ? (selected
        ? ((g.dribbleStreak?.[humanKey] ?? 0) >= MAX_DRIBBLE_STREAK && isCarrier(g, humanKey, selected.idx)
          ? '🚫 Üst üste 2 sürüş yaptın — bu tur sürüş yok, pas at ya da şut çek (sarı hücreler).'
          : '⚽ Hedef hücreye dokun — mavi: sürüş (1 hücre) · sarı: pas/şut · kırmızı: top kap')
        : (g.ball.owner === humanKey
          ? '🎯 Senin topun! Bir taşına dokunup pas at, sür ya da kaleye şut çek.'
          : g.ball.owner === aiKey || g.ball.owner === oppKey
            ? (g.tackleLockedFor === humanKey
              ? '⛔ Az önce topu kaptırdın — bu tur müdahale yapamazsın. Taşlarını yerleştir, pas yolunu kes.'
              : '🛡️ Rakipte top. Taşını seçip pas yolunu kes ya da komşu taşla topu kap.')
            : '🏃 Top boşta! Bir taşınla üstüne git ve sahiplen.'))
      : `⏳ ${isPractice ? 'Robo' : oppName} düşünüyor...`;

  return (
    <div className={styles.overlay}>
      <div className={styles.fxLayer}>
        <div className={styles.edgeGlowLeft} />
        <div className={styles.edgeGlowRight} />
      </div>

      {onClose && <button className={styles.closeBtn} onClick={onClose} aria-label="Kapat">✕</button>}
      <button className={styles.muteBtn} onClick={() => setMuted((m) => !m)} aria-label="Ses">
        {muted ? '🔇' : '🔊'}
      </button>

      <AnimatePresence>
        {achievement && (
          <motion.div
            className={styles.achievementToast}
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -60, opacity: 0 }}
          >
            <span className={styles.achIcon}>{achievement.icon}</span>
            <div>
              <strong>Rozet açıldı!</strong>
              <p>{achievement.name} — {achievement.desc}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>⚽ Habcare Gol <span className={styles.sub}>Grid Striker</span></h3>
          {mode !== 'practice' && (
            <div className={styles.presence}>
              <span className={`${styles.presenceDot} ${oppOnline ? styles.online : styles.offline}`} />
              {oppName} {oppOnline ? 'çevrimiçi' : 'çevrimdışı'}
            </div>
          )}
        </div>

        {error && <div className={styles.errorBar} onClick={() => setError(null)}>{error} (kapat)</div>}

        {/* ---- MENÜ ---- */}
        {mode === 'menu' && (
          <div className={styles.menu}>
            <RivalryPanel stats={stats} nameP0={nameP0} nameP1={nameP1} myKey={myKey} collapsible open={rivalryOpen} onToggle={toggleRivalry} />
            <button className={styles.menuCard} onClick={() => { sfx.select(); setMode('online'); }}>
              <span className={styles.menuIcon}>🏟️</span>
              <div>
                <strong>Canlı Düello</strong>
                <p>{oppName} ile sıra tabanlı saha satrancı</p>
                {game.status === 'playing' && <span className={styles.liveBadge}>Maç sürüyor!</span>}
              </div>
            </button>
            <button className={styles.menuCard} onClick={() => { sfx.select(); startPractice(); }}>
              <span className={styles.menuIcon}>🤖</span>
              <div>
                <strong>Antrenman</strong>
                <p>Robo rakibe karşı tek başına oyna</p>
              </div>
            </button>
            <JerseyPicker current={myJerseyId} onPick={selectJersey} />
            <JerseyPicker label="🤖 Antrenman rakibi" current={practiceJerseys.p1} onPick={selectOppJersey} />
            <div className={styles.diffPicker}>
              <span className={styles.jerseyLabel}>🎯 Robo zorluğu</span>
              <div className={styles.diffRow}>
                {AI_LEVEL_OPTS.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className={`${styles.diffChip} ${aiLevel === o.id ? styles.diffActive : ''}`}
                    onClick={() => selectAiLevel(o.id)}
                  >
                    <span className={styles.diffIcon}>{o.icon}</span>
                    <span>{o.name}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.rules}>
              <strong>Nasıl oynanır?</strong>
              <ul>
                <li>Sırayla tek hamle: taşını <b>1 adım</b> oynat, topu <b>sür</b>, ya da düz yönde <b>{RANGE} hücreye kadar pas/şut</b> at.</li>
                <li>Pasın yolunda rakip taş varsa <b>top kapılır</b>; kalede durursa <b>kurtarış</b> olur. Pas yolunu kapat!</li>
                <li>Topu, kaleye komşu taşınla <b>kapabilirsin</b>. Boştaki topu üstüne giderek <b>sahiplen</b>.</li>
                <li>Topu rakip kaleye sok — ilk <b>{WIN_GOALS} gol</b> kazanır.</li>
              </ul>
            </div>
          </div>
        )}

        {/* ---- LOBİ ---- */}
        {mode === 'online' && g.status === 'idle' && (
          <div className={styles.lobby}>
            <Board g={emptyGame()} perspective={myKey} interactive={false} jerseys={jerseyThemes} />
            <JerseyPicker current={myJerseyId} onPick={selectJersey} />
            <p className={styles.lobbyText}>
              {oppOnline ? `${oppName} hazır! Maçı başlatabilirsin.` : `${oppName} çevrimdışı. İkiniz de bu ekrandayken oynanır.`}
            </p>
            <div className={styles.buttonRow}>
              <button className={styles.primaryBtn} onClick={() => startMatch(myKey)} disabled={!oppOnline}>Maçı Başlat (sen başla)</button>
              <button className={styles.secondaryBtn} onClick={() => setMode('menu')}>Geri</button>
            </div>
          </div>
        )}

        {/* ---- OYUN ---- */}
        {((mode === 'online' && g.status !== 'idle') || isPractice) && (
          <>
            <div className={styles.scoreboard}>
              <div className={`${styles.scoreSide} ${g.turn === 'p0' && g.status === 'playing' ? styles.activeSide : ''}`}>
                <span className={styles.teamDot} style={{ background: jerseyThemes.p0.body, borderColor: jerseyThemes.p0.trim }} />
                <span className={styles.playerName}>{leftName()}</span>
                <span className={styles.score}>{g.scores.p0}</span>
              </div>
              <div className={styles.scoreCenter}>
                {g.status === 'finished' ? 'Bitti' : `İlk ${WIN_GOALS} gol`}
                {ballOwnerName && g.status === 'playing' && (
                  <span className={styles.possession}>⚽ {ballOwnerName}</span>
                )}
              </div>
              <div className={`${styles.scoreSide} ${g.turn === 'p1' && g.status === 'playing' ? styles.activeSide : ''}`}>
                <span className={styles.score}>{g.scores.p1}</span>
                <span className={styles.playerName}>{rightName()}</span>
                <span className={styles.teamDot} style={{ background: jerseyThemes.p1.body, borderColor: jerseyThemes.p1.trim }} />
              </div>
            </div>

            <div className={styles.boardArea}>
              <Board
                g={g}
                perspective={isPractice ? 'p0' : myKey}
                selected={selected}
                targets={targets}
                interactive={canPlay}
                onCellClick={handleCell}
                onPieceClick={handlePiece}
                celebrate={!!celebrate}
                celebrateSide={celebrate}
                jerseys={jerseyThemes}
              />
              <AnimatePresence>
                {banner && (
                  <motion.div
                    className={`${styles.banner} ${banner.kind === 'goal' ? styles.bannerGoal : styles.bannerInfo}`}
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 1.2, opacity: 0 }}
                  >
                    {banner.text}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {g.status === 'finished' ? (
              <div className={styles.finished}>
                <span className={styles.trophy}>🏆</span>
                <h4>{isPractice
                  ? (g.winner === 'p0' ? 'Tebrikler, kazandın! 🎉' : 'Robo kazandı 🤖')
                  : (iWon ? 'Tebrikler, kazandın! 🎉' : `${winnerName} kazandı!`)}</h4>
                <p>{g.scores.p0} - {g.scores.p1}</p>
                {!isPractice && <RivalryPanel stats={stats} nameP0={nameP0} nameP1={nameP1} myKey={myKey} compact />}
                <div className={styles.buttonRow}>
                  <button className={styles.primaryBtn} onClick={() => (isPractice ? startPractice() : startMatch(g.winner === myKey ? oppKey : myKey))}>
                    Yeniden Oyna
                  </button>
                  <button className={styles.secondaryBtn} onClick={leaveToMenu}>Menüye Dön</button>
                </div>
              </div>
            ) : (
              <div className={styles.controls}>
                {statusLine && <p className={`${styles.turnText} ${myTurn ? styles.myTurn : ''}`}>{statusLine}</p>}
                <button className={styles.linkBtn} onClick={leaveToMenu}>Maçtan Çık</button>
                {!isPractice && !oppOnline && <p className={styles.offlineWarning}>⚠ {oppName} bağlantısı kopmuş olabilir.</p>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GridStriker;
